import { useState } from "react";
import { MessageSquarePlus, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";

const CATEGORIES = [
  { key: "feature", label: "Feature idea" },
  { key: "bug", label: "Bug" },
  { key: "general", label: "General" },
];

export const FeedbackButton = () => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [category, setCategory] = useState("feature");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!text.trim()) { toast.error("Please write your suggestion first."); return; }
    setSending(true);
    try {
      await api.post("/suggestions", { text, category });
      toast.success("Thanks! Your suggestion was sent.");
      setText("");
      setOpen(false);
    } catch {
      toast.error("Could not send suggestion. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="feedback-fab"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-6 z-40 flex items-center gap-2 btn-brand text-white rounded-full pl-4 pr-5 py-3 shadow-lg shadow-[#007AFF]/20 hover:scale-[1.03] transition-transform"
        title="Send a suggestion"
      >
        <MessageSquarePlus size={18} />
        <span className="text-sm font-medium hidden sm:block">Suggestion</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Send us a suggestion</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Ideas, missing features, or something broken — we read every one.
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2 mt-1">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                data-testid={`feedback-cat-${c.key}`}
                onClick={() => setCategory(c.key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  category === c.key
                    ? "bg-[#007AFF] text-white border-[#007AFF]"
                    : "text-zinc-400 border-white/15 hover:border-white/30"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <Textarea
            data-testid="feedback-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What would make Link Website Pro better for you?"
            className="mt-2 min-h-[120px] bg-[#0A0A0A] border-white/15 text-white resize-none"
            maxLength={2000}
          />

          <button
            data-testid="feedback-submit"
            onClick={submit}
            disabled={sending}
            className="mt-1 w-full flex items-center justify-center gap-2 btn-brand text-white rounded-lg px-4 py-2.5 text-sm font-medium disabled:opacity-50"
          >
            {sending ? <><Loader2 size={15} className="animate-spin" /> Sending…</> : <><Send size={15} /> Send suggestion</>}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
};
